<template>
  <div/>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

const store = useStore<RootState>();

defineOptions({
  name: 'dataTranslateLanguagePages',
});

// Базовый тип для сообщений с ID перевода языковой страницы
type TranslateLanguagePageWithId = {
  idTranslateLanguagePage: number;
};

// Базовый тип для общих полей перевода языковой страницы
type TranslateLanguagePageCommonFields = {
  title: string;
  breadCrumb: string;
};

// Типы для WebSocket сообщений компонента translateLanguagePages
type TranslateLanguagePagesListMessage = WebSocketMessageWithFields<'language-page-translations' | 'translate-language-page-list', {
  translateLanguagePages: unknown[];
}>;
type TranslateLanguagePagesNotFoundMessage = WebSocketMessageEmpty<'language-page-item-list-not-found' | 'language-page-translations-not-found'>;
type TranslateLanguagePageCreateMessage = WebSocketMessageWithFields<'translate-language-page-create' | 'translate-language-page-create-success', TranslateLanguagePageWithId & {
  idLanguagePage: number;
  idLanguage: number;
  locked: boolean;
} & TranslateLanguagePageCommonFields>;
type TranslateLanguagePageUpdateMessage = WebSocketMessageWithFields<'translate-language-page-update' | 'translate-language-page-update-success', TranslateLanguagePageWithId & TranslateLanguagePageCommonFields>;
type TranslateLanguagePageDeleteMessage = WebSocketMessageWithFields<'translate-language-page-delete' | 'translate-language-page-delete-success', TranslateLanguagePageWithId>;
type TranslateLanguagePageLockMessage = WebSocketMessageWithFields<'translate-language-page-lock' | 'translate-language-page-lock-success', TranslateLanguagePageWithId>;
type TranslateLanguagePageUnlockMessage = WebSocketMessageWithFields<'translate-language-page-unlock' | 'translate-language-page-unlock-success', TranslateLanguagePageWithId>;
type TranslateLanguagePageUpdateTitleMessage = WebSocketMessageWithFields<'translate-language-page-update-title', {
  i18n: {
    pageName: string;
    title: string;
  };
}>;
type TranslateLanguagePageUpdateBreadCrumbMessage = WebSocketMessageWithFields<'translate-language-page-update-bread-crumb', {
  i18n: {
    pageName: string;
    breadCrumb: string;
  };
}>;

// Объединенный тип для всех сообщений
type TranslateLanguagePagesWebSocketMessage = 
  | TranslateLanguagePagesListMessage 
  | TranslateLanguagePagesNotFoundMessage 
  | TranslateLanguagePageCreateMessage 
  | TranslateLanguagePageUpdateMessage 
  | TranslateLanguagePageDeleteMessage 
  | TranslateLanguagePageLockMessage 
  | TranslateLanguagePageUnlockMessage 
  | TranslateLanguagePageUpdateTitleMessage 
  | TranslateLanguagePageUpdateBreadCrumbMessage;

// WebSocket message handler
const handleWebSocketMessage = (data: TranslateLanguagePagesWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'language-page-translations':
      store.dispatch('translateLanguagePages/wsSetTranslateLanguagePages', {
        translateLanguagePages: data.translateLanguagePages,
      });
      break;
    case 'language-page-item-list-not-found':
    case 'language-page-translations-not-found':
      store.dispatch('languagePage/wsSetEmptyLanguagePage');
      break;

    case 'translate-language-page-list':
      store.dispatch('translateLanguagePages/wsSetTranslateLanguagePages', {
        translateLanguagePages: data.translateLanguagePages,
      });
      break;

    case 'translate-language-page-create':
    case 'translate-language-page-create-success':
      store.dispatch('translateLanguagePages/wsTranslateLanguagePageCreate', {
        idTranslateLanguagePage: data.idTranslateLanguagePage,
        idLanguagePage: data.idLanguagePage,
        idLanguage: data.idLanguage,
        title: data.title,
        breadCrumb: data.breadCrumb,
        locked: data.locked,
      });
      break;

    case 'translate-language-page-update':
    case 'translate-language-page-update-success':
      store.dispatch('translateLanguagePages/wsTranslateLanguagePageUpdate', {
        idTranslateLanguagePage: data.idTranslateLanguagePage,
        title: data.title,
        breadCrumb: data.breadCrumb,
      });
      break;

    case 'translate-language-page-delete':
    case 'translate-language-page-delete-success':
      store.dispatch('translateLanguagePages/wsTranslateLanguagePageDelete', {
        idTranslateLanguagePage: data.idTranslateLanguagePage,
      });
      break;

    case 'translate-language-page-lock':
    case 'translate-language-page-lock-success':
      store.dispatch('translateLanguagePages/wsTranslateLanguagePageLock', {
        idTranslateLanguagePage: data.idTranslateLanguagePage,
      });
      break;

    case 'translate-language-page-unlock':
    case 'translate-language-page-unlock-success':
      store.dispatch('translateLanguagePages/wsTranslateLanguagePageUnlock', {
        idTranslateLanguagePage: data.idTranslateLanguagePage,
      });
      break;

    case 'translate-language-page-update-title':
      store.dispatch('i18n/updatePageTitle', {
        pageName: data.i18n.pageName,
        title: data.i18n.title,
      });
      break;

    case 'translate-language-page-update-bread-crumb':
      store.dispatch('i18n/updatePageBreadcrumb', {
        pageName: data.i18n.pageName,
        breadCrumb: data.i18n.breadCrumb,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);
</script>
